import { useState, useRef, useEffect } from "react"
import DashboardTopbar from "@/components/DashboardTopbar"
import Button from "@/components/Button"
import {
  getAvailablePieces,
  isValidBackRank,
  backRankToFen,
  positionNumberToBackRank,
} from "@/utils/chess960"
import type { BackRank, PieceType } from "@/utils/chess960"

type InputMode = "pieces" | "number"

interface PositionBuilderProps {
  onComplete: (fen: string) => void
  onCancel: () => void
}

const SQUARE_LABELS = ["a1", "b1", "c1", "d1", "e1", "f1", "g1", "h1"]

const EMPTY_RANK: BackRank = [null, null, null, null, null, null, null, null]

const PositionBuilder = ({ onComplete, onCancel }: PositionBuilderProps) => {
  const [mode, setMode] = useState<InputMode>("pieces")
  const [rank, setRank] = useState<BackRank>([...EMPTY_RANK])
  const [pickerSlot, setPickerSlot] = useState<number | null>(null)
  const [positionNumber, setPositionNumber] = useState("")
  const [positionError, setPositionError] = useState<string | null>(null)
  const pickerRef = useRef<HTMLDivElement>(null)

  const valid = isValidBackRank(rank)
  const placedCount = rank.filter((p) => p !== null).length

  const switchMode = (newMode: InputMode) => {
    if (newMode === mode) return
    setMode(newMode)
    setRank([...EMPTY_RANK])
    setPickerSlot(null)
    setPositionNumber("")
    setPositionError(null)
  }

  const handlePositionNumberChange = (value: string) => {
    setPositionNumber(value)
    setPositionError(null)

    if (value === "") {
      setRank([...EMPTY_RANK])
      return
    }

    const num = parseInt(value, 10)
    if (isNaN(num) || String(num) !== value.trim() || num < 1 || num > 960) {
      setPositionError("Enter a number from 1 to 960")
      setRank([...EMPTY_RANK])
      return
    }

    setRank(positionNumberToBackRank(num))
  }

  // Close picker on outside click. Uses mousedown (not click) so the event
  // fires before onClick on slot buttons — this lets tapping a different slot
  // close the current picker and open the new one in the correct order.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node)
      ) {
        setPickerSlot(null)
      }
    }
    if (pickerSlot !== null) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [pickerSlot])

  const handleSlotClick = (index: number) => {
    if (rank[index] !== null) {
      // Clear the slot
      const newRank = [...rank]
      newRank[index] = null
      setRank(newRank)
      setPickerSlot(null)
    } else {
      setPickerSlot(pickerSlot === index ? null : index)
    }
  }

  const handlePieceSelect = (index: number, piece: PieceType) => {
    const newRank = [...rank]
    newRank[index] = piece
    setRank(newRank)
    setPickerSlot(null)
  }

  const handleStart = () => {
    if (valid) {
      onComplete(backRankToFen(rank))
    }
  }

  return (
    <>
      <DashboardTopbar title="Set up starting position">
        <Button size="sm" style="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </DashboardTopbar>

      <main className="flex flex-1 flex-col items-center px-4 py-8 sm:px-6">
        <div className="w-full max-w-lg">
          {/* Mode toggle */}
          <div className="mb-6 flex justify-center">
            <div className="inline-flex rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                aria-pressed={mode === "pieces"}
                onClick={() => switchMode("pieces")}
                className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                  mode === "pieces"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Place pieces
              </button>
              <button
                type="button"
                aria-pressed={mode === "number"}
                onClick={() => switchMode("number")}
                className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                  mode === "number"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Position number
              </button>
            </div>
          </div>

          {mode === "number" && (
            <div className="mb-6">
              <label
                htmlFor="position-number"
                className="mb-1 block text-center text-sm text-gray-500"
              >
                Enter the Chess960 position number (1–960)
              </label>
              <input
                id="position-number"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={positionNumber}
                onChange={(e) => handlePositionNumberChange(e.target.value)}
                placeholder="e.g. 518"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-center text-lg shadow-sm focus:border-chessdrop-500 focus:ring-chessdrop-500 focus:outline-none"
              />
              {positionError && (
                <p className="mt-1 text-center text-xs text-red-500">
                  {positionError}
                </p>
              )}
            </div>
          )}

          {mode === "pieces" && (
            <p className="mb-6 text-center text-sm text-gray-500">
              Tap each square to place a piece. Tap a placed piece to remove
              it.
            </p>
          )}

          {/* Back rank slots */}
          <div className="mx-auto mb-2 grid w-full grid-cols-8 gap-0 rounded-md shadow-sm">
            {rank.map((piece, index) => {
              const isDark = index % 2 === 0
              const isFirst = index === 0
              const isLast = index === 7

              return (
                <div key={index} className="min-w-0">
                  <button
                    type="button"
                    disabled={mode === "number"}
                    aria-label={`${SQUARE_LABELS[index]}: ${piece ?? "empty"}`}
                    onClick={() => handleSlotClick(index)}
                    className={`flex aspect-square min-w-0 w-full items-center justify-center transition-colors ${
                      isDark
                        ? "bg-[#c56c1a] text-amber-50"
                        : "bg-[#ffd17f] text-amber-900"
                    } ${
                      isFirst ? "rounded-l-md" : ""
                    } ${
                      isLast ? "rounded-r-md" : ""
                    } ${
                      pickerSlot === index
                        ? "ring-2 ring-inset ring-chessdrop-500"
                        : ""
                    } ${
                      mode === "number" ? "cursor-default" : ""
                    }`}
                  >
                    {piece ? (
                      <span className="text-xl font-bold sm:text-2xl">
                        {piece}
                      </span>
                    ) : (
                      <span
                        className={`text-lg ${
                          isDark ? "text-amber-200/40" : "text-amber-800/20"
                        }`}
                      >
                        {mode === "pieces" ? "+" : ""}
                      </span>
                    )}
                  </button>
                </div>
              )
            })}
          </div>

          {/* Piece picker — rendered below the board in normal flow (pieces mode only) */}
          {mode === "pieces" &&
            pickerSlot !== null &&
            (() => {
              const available = getAvailablePieces(rank, pickerSlot)
              return available.length > 0 ? (
                <div
                  ref={pickerRef}
                  className="mb-2 flex items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
                >
                  {available.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePieceSelect(pickerSlot, p)
                      }}
                      className="flex h-10 w-10 items-center justify-center rounded-md text-sm font-bold text-gray-800 hover:bg-chessdrop-500 hover:text-white sm:h-11 sm:w-11 sm:text-base"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              ) : null
            })()}

          {/* Square labels */}
          <div className="mx-auto mb-8 grid w-full grid-cols-8 gap-0">
            {SQUARE_LABELS.map((label) => (
              <span
                key={label}
                className="text-center text-xs text-gray-400"
              >
                {label}
              </span>
            ))}
          </div>

          {/* Progress indicator (pieces mode only) */}
          {mode === "pieces" && (
            <p className="mb-4 text-center text-xs text-gray-400">
              {placedCount}/8 pieces placed
            </p>
          )}

          {/* Start button */}
          <Button
            size="lg"
            style="primary"
            fullWidth
            disabled={!valid}
            onClick={handleStart}
          >
            Start Game
          </Button>
        </div>
      </main>
    </>
  )
}

export default PositionBuilder
